import React, { useImperativeHandle } from "react";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { RenderCurrentScaleProps, zoomPlugin } from "@react-pdf-viewer/zoom";
import { printPlugin } from "@react-pdf-viewer/print";
import { FileViewerHandle, FileViewerAdapterProps } from "../FilePreviewer.types";
import { CSS_CLASSES } from "../FilePreviewer.config";
import { file } from "@babel/types";

const PdfViewerAdapter = React.forwardRef<FileViewerHandle, FileViewerAdapterProps>(
  ({ filePath, fileType, fileName, onZoomChange }, ref) => {
    // Print & Zoom Plugins
    const printPluginInstance = printPlugin();
    const zoomPluginInstance = zoomPlugin();
    const { print } = printPluginInstance;
    const { CurrentScale } = zoomPluginInstance;
    // Zoom State
    const [zoomLevel, setZoomLevel] = React.useState<number>(1);

    // Notify parent of zoom changes
    const handleScaleChange = (props: RenderCurrentScaleProps) => {
      setZoomLevel(props.scale);
      if (onZoomChange) {
        onZoomChange(Math.round(props.scale * 100));
      }
      return <></>; // Render an empty fragment
    };

    useImperativeHandle(ref, () => ({
      download: () => {
        const link = document.createElement("a");
        link.href = filePath;
        link.download = fileName ?? "document.pdf";
        link.click();
      },
      zoomIn: () => {
        zoomPluginInstance.zoomTo(zoomLevel + 0.1);
      },
      zoomOut: () => zoomPluginInstance.zoomTo(zoomLevel - 0.1),
      zoomReset: () => zoomPluginInstance.zoomTo(1),
      print: () => print(),
    }));

    return (
      <div className={`${CSS_CLASSES.REACT_FILE_VIEWER} ${CSS_CLASSES.DOCUMENT_ZOOM_CONTAINER}`}>
        <div style={{ height: "100%", minHeight: 500 }}>
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
            <Viewer
              key={filePath}
              fileUrl={filePath}
              plugins={[zoomPluginInstance, printPluginInstance]}
            />
            <CurrentScale>{handleScaleChange}</CurrentScale>
          </Worker>
        </div>
      </div>
    );
  }
);

export default PdfViewerAdapter;
